/** What GET /api/me/profile says about the signed-in person (apps/server/src/profile.mjs). */
export interface MyProfile {
  email: string;
  displayName: string;
  title: string | null;
  about: string | null;
  /** 0 is Sunday, 1 is Monday. */
  weekStartsOn: WeekStartsOn;
  /** Each app's tab keys in the order this person dragged them into, by app key. */
  tabOrder: TabOrder;
  hasPhoto: boolean;
}

export type WeekStartsOn = 0 | 1;

export type TabOrder = Readonly<Record<string, readonly string[]>>;

/** The server's limit; the library cropper's 512px WebP is normally far smaller. */
export const MAX_PHOTO_BYTES = 256 * 1024;
export const PHOTO_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** Client-side only: the server repeats these checks against the actual bytes. */
export function photoInputError(file: Pick<File, 'size' | 'type'>): string | null {
  if (file.size === 0) return 'Choose a photo to upload.';
  if (!(PHOTO_TYPES as readonly string[]).includes(file.type)) {
    return 'Photos must be PNG, JPEG or WebP images.';
  }
  if (file.size > MAX_PHOTO_BYTES) return 'This photo is still larger than 256 KB after cropping.';
  return null;
}

export function parseProfile(value: unknown): MyProfile {
  if (
    !isRecord(value) ||
    typeof value.email !== 'string' ||
    typeof value.displayName !== 'string' ||
    !isOptionalText(value.title) ||
    !isOptionalText(value.about) ||
    (value.weekStartsOn !== 0 && value.weekStartsOn !== 1) ||
    typeof value.hasPhoto !== 'boolean'
  ) {
    throw new Error('The profile response was invalid.');
  }
  return {
    email: value.email,
    displayName: value.displayName,
    title: value.title ?? null,
    about: value.about ?? null,
    weekStartsOn: value.weekStartsOn,
    tabOrder: parseTabOrder(value.tabOrder),
    hasPhoto: value.hasPhoto,
  };
}

/** The saved tab orders; anything malformed is dropped, so the app's own order shows. */
function parseTabOrder(value: unknown): TabOrder {
  if (!isRecord(value) || Array.isArray(value)) return {};
  const out: Record<string, readonly string[]> = {};
  for (const [app, tabs] of Object.entries(value)) {
    if (Array.isArray(tabs) && tabs.every((t) => typeof t === 'string')) out[app] = tabs;
  }
  return out;
}

/** The server's `{ error }` message, or the fallback. */
export function errorMessage(payload: unknown, fallback: string): string {
  return isRecord(payload) && typeof payload.error === 'string' && payload.error !== ''
    ? payload.error
    : fallback;
}

function isOptionalText(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || typeof value === 'string';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
